import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useStyles } from '../ExaminationTable/ExaminationTable.style';
import TablePlaceholder from '../ExaminationTable/Helpers/TablePlaceholder';
import {
  getWorklistColumnWidths,
  getColumnCellStyle,
  getTableTransitionStyle,
} from '../examTableColumnWidths';
import WorklistTableRows from './WorklistTableRows';

function WorklistTable({ tableEl, expandedPatientList, drawerWidth, worklist: worklistProp }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const { worklist: storeWorklist, worklistInfo } = useSelector((state) => state.examinations);
  const { priors_number_limit, priors_years_limit } = useSelector(
    (state) => state.global_settings.user_settings,
  );
  const filters = useSelector((state) => state.filter);
  const worklist = worklistProp ?? storeWorklist;
  const [orderBy, setOrderBy] = useState('scheduled_date');
  const [order, setOrder] = useState('ASC');

  const columnWidths = useMemo(
    () => getWorklistColumnWidths(expandedPatientList, drawerWidth),
    [expandedPatientList, drawerWidth],
  );

  const tableColumns = useMemo(
    () => [
      {
        key: 'expand',
        name: 'expand',
        label: '',
        width: columnWidths.expand,
        sort: false,
        align: 'center',
        padding: 'none',
      },
      {
        key: 'status',
        name: 'status',
        label: '',
        width: columnWidths.status,
        sort: false,
        align: 'left',
        padding: 'none',
      },
      {
        key: 'patient_name',
        name: 'patient_name',
        label: t('Table.name'),
        width: columnWidths.patient_name,
        sort: true,
        align: 'left',
        padding: 'none',
      },
      ...(expandedPatientList
        ? [
            {
              key: 'patient_birth_date',
              name: 'patient_birth_date',
              label: t('Table.dob'),
              width: columnWidths.patient_birth_date,
              sort: true,
              align: 'left',
              padding: 'none',
            },
            {
              key: 'patient_id',
              name: 'patient_id',
              label: t('Table.patient_id'),
              width: columnWidths.patient_id,
              sort: true,
              align: 'left',
              padding: 'none',
            },
          ]
        : []),
      {
        key: 'scheduled_date',
        name: 'scheduled_date',
        label: t('Table.scheduled_date'),
        width: columnWidths.scheduled_date,
        sort: true,
        align: 'left',
        padding: 'none',
      },
    ],
    [columnWidths, expandedPatientList],
  );

  const getSortValue = (patient, columnName) => {
    if (columnName === 'patient_birth_date') {
      return patient.patient_birth_date || patient.birth_date || '';
    }
    return patient[columnName] ?? '';
  };

  const sortedWorklist = useMemo(() => {
    const direction = order === 'ASC' ? 1 : -1;

    return [...worklist].sort((a, b) => {
      const aVal = getSortValue(a, orderBy);
      const bVal = getSortValue(b, orderBy);

      if (aVal === bVal) return 0;
      if (aVal == null || aVal === '') return 1;
      if (bVal == null || bVal === '') return -1;

      if (orderBy === 'scheduled_date' || orderBy === 'patient_birth_date') {
        return (new Date(aVal).getTime() - new Date(bVal).getTime()) * direction;
      }

      return String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' }) * direction;
    });
  }, [worklist, order, orderBy]);

  const createSortHandler = (columnName) => () => {
    const isDesc = orderBy === columnName && order === 'DESC';
    setOrderBy(columnName);
    setOrder(isDesc ? 'ASC' : 'DESC');
  };

  const sortInfo = (columnName, isActive) => {
    const columnLabels = {
      patient_name: t('Table.name'),
      patient_birth_date: t('Table.dob'),
      patient_id: t('Table.patient_id'),
      scheduled_date: t('Table.scheduled_date'),
    };
    const orderText = order === 'ASC' ? t('DatePicker.asc') : t('DatePicker.des');

    if (isActive) {
      return t('DatePicker.sorted_in', {
        column: columnLabels[columnName] || columnName,
        sorted: orderText,
      });
    }

    return columnName === 'patient_name' ? t('DatePicker.no_sort') : t('DatePicker.upload_date');
  };

  return (
    <TableContainer ref={tableEl} style={{ maxHeight: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
      {worklistInfo || storeWorklist.length === 0 || sortedWorklist.length === 0 ? (
        <TablePlaceholder colSpan={tableColumns.length} centered>
          <Typography className={classes.table_placeholder}>
            {sortedWorklist.length === 0 && storeWorklist.length > 0
              ? filters.search
                ? t('Settings.worklist_empty_search')
                : t('Settings.filtered_worklist_empty')
              : worklistInfo === 'empty'
                ? t('Settings.worklist_empty', { priors_number_limit, priors_years_limit })
                : worklistInfo}
          </Typography>
        </TablePlaceholder>
      ) : (
        <Table stickyHeader aria-label='worklist-table' style={getTableTransitionStyle(drawerWidth)}>
          <colgroup>
            {tableColumns.map((column) => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {tableColumns.map((head, i) => (
                <TableCell
                  key={head.key + i}
                  align={head.align}
                  padding={head.padding}
                  sortDirection={orderBy === head.name ? order.toLowerCase() : false}
                  style={getColumnCellStyle(head.width, {
                    noPadding: head.key === 'status' || head.key === 'expand',
                  })}>
                  {head.sort ? (
                    <Tooltip
                      arrow
                      placement='top'
                      title={
                        <Typography style={{ letterSpacing: '0.1rem', fontSize: '12px' }}>
                          {sortInfo(head.name, orderBy === head.name)}
                        </Typography>
                      }>
                      <TableSortLabel
                        active={orderBy === head.name}
                        direction={orderBy === head.name ? order.toLowerCase() : 'asc'}
                        onClick={createSortHandler(head.name)}>
                        {head.label}
                      </TableSortLabel>
                    </Tooltip>
                  ) : (
                    head.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <WorklistTableRows patients={sortedWorklist} tableColumns={tableColumns} />
        </Table>
      )}
    </TableContainer>
  );
}

export default WorklistTable;
